import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
import { DefaultSets } from '../../../utils/postScoreConstants';
import { pos2Won, position1winner, resolvePlayerIdForPosition, resultToScore, shouldAdvanceFocusAfterScore } from '../../../utils/postScore';
import { getMatchOutComeFromCompletionType, OUTCOMES_TO_ALLOW_SCORING, OUTCOMES_WITH_NO_WINNER, OUTCOMES_WITH_WINNER_BY_TEAM_OR_PLAYER_SELECTOR } from './consts';
import { isTennisSportTypeEvent } from '../../../utils/events';
import { resolveIsWinnerAndIsCompletedByOutcomeAndScores, resolveWinnerAndLoserByOutcomeAndScores } from '../../../utils/courtSchedular';
import { sportTypeIds } from '../../../utils/eventConstants';
import useScoreAutoAdvance from '../../../components/hooks/useScoreAutoAdvance';

const useGetMatchPost = ({ match, playersDictByMemberId, teamsDict, sportTypeId }) => {
    const [date, setDate] = useState(null);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isWinner, setIsWinner] = useState(false);
    const [winningTeam, setWinningTeam] = useState(null);
    const [losingTeam, setLosingTeam] = useState(null);
    const [matchOutcome, setMatchOutcome] = useState(null);
    const [sets, setSets] = useState(DefaultSets);
    const [losingTeamOrPlayerSelected, setLosingTeamOrPlayerSelected] = useState(null);
    const [winningTeamOrPlayerSelected, setWinningTeamOrPlayerSelected] = useState(null);
    const [areScoresChanged, setAreScoresChanged] = useState(false);

    const { attachRef, onValueChange, onKeyDown: onScoreKeyDown, currentSet, setCurrentSet } = useScoreAutoAdvance({
        enabled: sportTypeId !== sportTypeIds.PICKLEBALL,
        sportTypeId,
    });

    const handleScoreKeyDown = (set, key, event) => {
        onScoreKeyDown({ set, key, event, newSets: sets, shouldAdvance: true });
    };

    const team1ID = resolvePlayerIdForPosition(match.position1, match, playersDictByMemberId);
    const team2ID = resolvePlayerIdForPosition(match.position2, match, playersDictByMemberId);

    const updateMatchStatus = useCallback((newMatchOutcome, newSets, newWinningTeamOrPlayerSelected, newLosingTeamOrPlayerSelected) => {
        const { isWinner: newIsWinner, isCompleted: newIsCompleted } = resolveIsWinnerAndIsCompletedByOutcomeAndScores(
            newMatchOutcome, newSets,
        );
        setIsWinner(newIsWinner);
        setIsCompleted(newIsCompleted);
        const { winningTeam: newWinningTeam, losingTeam: newLosingTeam } = resolveWinnerAndLoserByOutcomeAndScores(
            newMatchOutcome,
            newSets,
            newWinningTeamOrPlayerSelected,
            newLosingTeamOrPlayerSelected,
            team1ID,
            team2ID,
        );
        setWinningTeam(newWinningTeam);
        setLosingTeam(newLosingTeam);
    }, [team1ID, team2ID]);

    const handleInputChange = useCallback((set, key, event) => {
        const { value } = event.target;
        const newSets = _.cloneDeep(sets);
        const setIndex = _.findIndex(newSets, { id: set.id });
        newSets[setIndex][key] = value;
        setSets(newSets);
        setAreScoresChanged(true);
        updateMatchStatus(matchOutcome, newSets, winningTeamOrPlayerSelected, losingTeamOrPlayerSelected);
        const isTieBreak = key === 'pos1TieBrk' || key === 'pos2TieBrk';
        const shouldAdvance = shouldAdvanceFocusAfterScore({ value, isTieBreak, setId: set.id });
        onValueChange({ set, key, value, isTieBreak, newSets, shouldAdvance });
    }, [updateMatchStatus, sets, matchOutcome, winningTeamOrPlayerSelected, losingTeamOrPlayerSelected, onValueChange]);

    const handleSwapPositions = useCallback(() => {
        const setOne = sets.find(set => set.id === 0);
        const setTwo = sets.find(set => set.id === 1);
        const setThree = sets.find(set => set.id === 2);

        const updatedSets = [
            { id: 0, pos1Score: setOne.pos2Score, pos2Score: setOne.pos1Score, pos1TieBrk: setOne.pos2TieBrk, pos2TieBrk: setOne.pos1TieBrk },
            { id: 1, pos1Score: setTwo.pos2Score, pos2Score: setTwo.pos1Score, pos1TieBrk: setTwo.pos2TieBrk, pos2TieBrk: setTwo.pos1TieBrk },
            { id: 2, pos1Score: setThree.pos2Score, pos2Score: setThree.pos1Score, pos1TieBrk: setThree.pos2TieBrk, pos2TieBrk: setThree.pos1TieBrk },
        ];

        setSets(updatedSets);
        setAreScoresChanged(true);
        updateMatchStatus(matchOutcome, updatedSets, winningTeamOrPlayerSelected, losingTeamOrPlayerSelected);
    }, [updateMatchStatus, sets, matchOutcome, winningTeamOrPlayerSelected, losingTeamOrPlayerSelected]);

    useEffect(() => {
        setDate(moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ'));
    }, []);

    const getWinnerLoserSetData = useCallback(() => {
        if (!OUTCOMES_TO_ALLOW_SCORING.includes(matchOutcome)) {
            return {};
        }

        const isPosition1Winner = winningTeam === team1ID;
        const isTennisEvent = isTennisSportTypeEvent(sportTypeId);

        return sets.reduce((acc, set, index) => {
            const setId = `set${index + 1}`;
            const winnerSet = isPosition1Winner ? parseInt(set.pos1Score) : parseInt(set.pos2Score);
            const loserSet = !isPosition1Winner ? parseInt(set.pos1Score) : parseInt(set.pos2Score);

            let tiebreaker = null;
            if (isTennisEvent) {
                // Our system only saves the losers tiebreaker score in the database
                tiebreaker = pos2Won(set) ? set.pos1TieBrk : set.pos2TieBrk;
            }

            acc[setId] = { winnerSet, loserSet, tiebreaker };
            return acc;
        }, {});
    }, [sets, winningTeam, losingTeam, matchOutcome, sportTypeId]);

    useEffect(() => {
        if (match?.result) {
            setDate(moment(match.result.date).format('YYYY-MM-DDTHH:mm:ss.SSSZ'));
            const outcomeFromMatch = getMatchOutComeFromCompletionType(match.result.completionType, match?.result.outcome);
            setMatchOutcome(outcomeFromMatch);
            let newLosingTeam = null;
            let newWinningTeam = null;
            if (!OUTCOMES_WITH_NO_WINNER.includes(outcomeFromMatch) || match.result?.isWinner) {
                if (position1winner(match, teamsDict, playersDictByMemberId)) {
                    newLosingTeam = team2ID;
                    newWinningTeam = team1ID;
                } else {
                    newLosingTeam = team1ID;
                    newWinningTeam = team2ID;
                }
            }
            setWinningTeamOrPlayerSelected(newWinningTeam);
            setLosingTeamOrPlayerSelected(newLosingTeam);
            const setsFromMatch = resultToScore(match, teamsDict, playersDictByMemberId);
            setSets(setsFromMatch);
            updateMatchStatus(outcomeFromMatch, setsFromMatch, newWinningTeam, newLosingTeam);
        }
    }, [match, updateMatchStatus]);

    const handleLosingTeamOrPlayerSelected = useCallback((value, isFromPreSelection = false) => {
        setLosingTeamOrPlayerSelected(value);
        const newWinningTeamOrPlayerSelected = value === String(team1ID) ? team2ID : team1ID;
        setWinningTeamOrPlayerSelected(newWinningTeamOrPlayerSelected);
        setAreScoresChanged(!isFromPreSelection);
        updateMatchStatus(matchOutcome, sets, newWinningTeamOrPlayerSelected, value);
    }, [matchOutcome, sets, team1ID, team2ID, updateMatchStatus]);

    const handleOutcomeChange = useCallback((selection) => {
        setMatchOutcome(selection);
        let newLosingTeamOrPlayerSelected = losingTeamOrPlayerSelected;
        let newWinningTeamOrPlayerSelected = winningTeamOrPlayerSelected;
        if (!OUTCOMES_WITH_WINNER_BY_TEAM_OR_PLAYER_SELECTOR.includes(matchOutcome) && OUTCOMES_WITH_WINNER_BY_TEAM_OR_PLAYER_SELECTOR.includes(selection)) {
            newLosingTeamOrPlayerSelected = null;
            newWinningTeamOrPlayerSelected = null;
        }
        setLosingTeamOrPlayerSelected(newLosingTeamOrPlayerSelected);
        setWinningTeamOrPlayerSelected(newWinningTeamOrPlayerSelected);
        setAreScoresChanged(true);
        updateMatchStatus(selection, sets, newWinningTeamOrPlayerSelected, newLosingTeamOrPlayerSelected);
    }, [matchOutcome, sets, losingTeamOrPlayerSelected, winningTeamOrPlayerSelected, updateMatchStatus]);

    return {
        match,
        date,
        isWinner,
        winningTeam,
        losingTeam,
        sets,
        isCompleted,
        areScoresChanged,
        matchOutcome,
        handleInputChange,
        handleSwapPositions,
        handleLosingTeamOrPlayerSelected,
        getWinnerLoserSetData,
        losingTeamOrPlayerSelected,
        winningTeamOrPlayerSelected,
        handleOutcomeChange,
        attachRef,
        handleScoreKeyDown,
        currentSet,
        setCurrentSet,
    };
};

export default useGetMatchPost;
